import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ApiClient from 'dashboard/api/ApiClient';
import PermissionSetsAPI from 'dashboard/api/permissionSets';

export function useAgentPermissionOptions({ includeOwn = true } = {}) {
  const store = useStore();
  const { t } = useI18n();
  const { accountId } = useAccount();
  const { isEnterprise } = useConfig();
  const enterprise = Boolean(isEnterprise);
  const scope = computed(
    () => `${accountId.value}:${store.getters.getCurrentUserID}`
  );
  const feature = flag =>
    store.getters['accounts/isFeatureEnabledonAccount'](accountId.value, flag);
  const ownEnabled = computed(() => feature(FEATURE_FLAGS.CUSTOM_ROLES_V2));
  const nativeEnabled = computed(
    () => enterprise && feature(FEATURE_FLAGS.CUSTOM_ROLES)
  );
  const own = ref([]);
  const native = ref([]);
  const loading = ref(false);
  const error = ref(false);
  let revision = 0;

  const load = async () => {
    revision += 1;
    const captured = revision;
    own.value = [];
    native.value = [];
    loading.value = true;
    error.value = false;
    try {
      const [ownResponse, nativeResponse] = await Promise.all([
        includeOwn && ownEnabled.value
          ? new PermissionSetsAPI(accountId.value).get()
          : null,
        nativeEnabled.value
          ? new ApiClient(`accounts/${accountId.value}/custom_roles`).get()
          : null,
      ]);
      if (captured !== revision) return;
      own.value = ownResponse?.data || [];
      native.value = nativeResponse?.data || [];
    } catch {
      if (captured === revision) error.value = true;
    } finally {
      if (captured === revision) loading.value = false;
    }
  };
  watch([scope, ownEnabled, nativeEnabled], load, { immediate: true });
  onUnmounted(() => {
    revision += 1;
  });

  const roles = computed(() => [
    { id: 'administrator', label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') },
    { id: 'agent', label: t('AGENT_MGMT.AGENT_TYPES.AGENT') },
    ...own.value.map(record => ({
      id: `permission_set:${record.id}`,
      label: t('PERMISSION_SETS.ASSIGNMENT.OWN', { name: record.name }),
    })),
    ...native.value.map(record => ({
      id: `native:${record.id}`,
      label: t('PERMISSION_SETS.ASSIGNMENT.NATIVE', { name: record.name }),
    })),
  ]);
  return {
    roles,
    native,
    ownEnabled,
    nativeEnabled,
    enterprise,
    loading,
    error,
    load,
    scope,
  };
}
